<template>
  <div
    ref="select"
    class="ui-select"
    :class="{
      'ui-select--disabled': disabled,
      'ui-select--single': !multi,
      'ui-select--error': error,
    }"
  >
    <div class="ui-select__trigger" @click="toggleOpen">
      <div class="ui-select__value">
        <template v-if="multi">
          <div v-if="selected.length" class="ui-select__tags">
            <template v-for="(option, index) in selected">
              <slot name="tag" :option="option" :index="index" :remove="() => handleSelectOption(option)">
                <div :key="resolveOptionId(option)" class="ui-select__tag" :title="resolveLabel(option)">
                  <span :title="resolveLabel(option)">{{ resolveLabel(option) }}</span>
                  <button type="button" class="ui-select__tag-remove" @click.stop="handleSelectOption(option)">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1 1L9 9M1 9L9 1L1 9Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </slot>
            </template>
          </div>

          <span v-else class="ui-select__placeholder">
            {{ placeholder }}
          </span>
        </template>

        <template v-else>
          <slot v-if="selected" name="selected" :option="selected">
            <span class="ui-select__single-value">
              {{ resolveLabel(selected) }}
            </span>
          </slot>

          <span v-else class="ui-select__placeholder">
            {{ placeholder }}
          </span>
        </template>
      </div>

      <svg
        v-if="clearable && hasValue"
        class="ui-select__clear"
        @click.stop="clear"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        role="img"
        aria-hidden="true"
      >
        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"></path>
      </svg>

      <svg
        class="ui-select__arrow"
        :class="{ 'ui-select__arrow--open': isOpen }"
        width="9"
        height="5"
        viewBox="0 0 9 5"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4.13455 4.84913L0 0.728442L0.730893 0L4.5 3.75647L8.26911 0L9 0.728442L4.86545 4.84913C4.66362 5.05029 4.33638 5.05029 4.13455 4.84913Z"
          fill="currentColor"
        />
      </svg>
    </div>

    <transition name="fade">
      <div v-if="isOpen" class="ui-select__dropdown" @click.stop>
        <div v-if="searchable" class="ui-select__search">
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="ui-select__search-input"
            type="text"
            :placeholder="searchPlaceholder"
            autocomplete="off"
            @input="handleSearchInput"
            @keydown.esc.prevent="close"
          />
        </div>

        <div ref="contentSelect" class="ui-select__content" @scroll="handleDropdownScroll">
          <div v-if="searchable && !displayedOptions.length" class="ui-select__state">
            <slot name="empty">
              Ничего не найдено
            </slot>
          </div>

          <div v-else class="ui-select__list">
            <div
              v-for="(option, index) in displayedOptions"
              :key="resolveOptionId(option)"
              class="ui-select__option"
              :class="{
                _selected: isSelected(option),
              }"
              @click="handleSelectOption(option)"
            >
              <slot name="option" :option="option" :is-selected="isSelected(option)" :index="index">
                <div class="ui-select__option-inner">
                  <span v-if="multi" class="ui-select__option-check">
                    <svg
                      class="ui-select__option-check-icon"
                      :class="{ _visible: isSelected(option) }"
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M10.4232 0.945312L11.3346 1.6368L4.83112 7.88175C4.71542 7.99286 4.54958 8.05642 4.37542 8.05642C4.20127 8.05642 4.03543 7.99286 3.91972 7.88175L0.667969 4.75928L1.57937 4.06779L4.37542 6.75269L10.4232 0.945312Z"
                        fill="#00b0bd"
                      />
                    </svg>
                  </span>
                  <span class="ui-select__option-label">
                    {{ resolveLabel(option) }}
                  </span>
                </div>
              </slot>
            </div>

            <div v-if="loading" class="ui-select__append-loader">
              <slot name="loading">
                Загрузка...
              </slot>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
function debounce(fn, delay = 300) {
  let timeout = null

  return function(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

function defaultMapOption(item) {
  return {
    label: item?.label,
    id: item?.id,
  }
}

export default {
  name: 'ui-select',
  emits: ['input', 'change', 'open', 'close', 'error'],
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [Array, Object, String, Number],
      default: null,
    },
    options: {
      type: [Array, Function],
      default: () => [],
    },
    multi: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Выберите значение',
    },
    searchPlaceholder: {
      type: String,
      default: 'Поиск',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    closeOnSelect: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    debounceDelay: {
      type: Number,
      default: 800,
    },
    initialPage: {
      type: Number,
      default: 1,
    },
    initialLimit: {
      type: Number,
      default: 10,
    },
    preloadImmediately: {
      type: Boolean,
      default: true,
    },
    resolveOptionId: {
      type: Function,
      default: option => option.id,
    },
    resolveLabel: {
      type: Function,
      default: option => option.label,
    },
    resolveRequest: {
      type: Function,
      default: null,
    },
    resolveResponse: {
      type: Function,
      default: null,
    },
    mapOption: {
      type: Function,
      default: defaultMapOption,
    },
    minSearchLength: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      searchQuery: '',
      remoteOptions: [],
      paginationState: {
        currentPage: 1,
        pageCount: 1,
      },
      requestId: 0,
      selectedCache: new Map(),
    }
  },
  created() {
    this.debouncedFetch = debounce(() => {
      this.loadOptions({
        currentPage: 1,
        append: false,
      })
    }, this.debounceDelay)

    if (this.isAsyncMode && this.preloadImmediately) {
      this.loadOptions({
        currentPage: this.initialPage,
        append: false,
      })
    }
  },
  mounted() {
    document.addEventListener('click', this.closeOnOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeOnOutside)
  },
  computed: {
    isAsyncMode() {
      return typeof this.options === 'function'
    },
    resolvedOptions() {
      if (!Array.isArray(this.options)) {
        return []
      }

      return this.options.map(this.mapOption)
    },
    displayedOptions() {
      if (this.isAsyncMode) {
        return this.remoteOptions
      }

      if (!this.searchable) {
        return this.resolvedOptions
      }

      const query = this.normalizedQuery

      if (!query || query.length < this.minSearchLength) {
        return this.resolvedOptions
      }

      return this.resolvedOptions.filter(option => {
        return this.normalizeSearchValue(this.resolveLabel(option)).includes(query)
      })
    },
    normalizedQuery() {
      return this.normalizeSearchValue(this.searchQuery.trim())
    },
    selected() {
      const findOption = id => {
        if (this.selectedCache.has(id)) return this.selectedCache.get(id)
        return this.displayedOptions.find(o => this.resolveOptionId(o) === id)
      }

      if (this.multi) {
        const ids = Array.isArray(this.value) ? this.value : []
        return ids.map(findOption).filter(Boolean)
      }

      if (this.value == null) return null
      return findOption(this.value) ?? null
    },
    hasNextPage() {
      return this.paginationState.currentPage < this.paginationState.pageCount
    },
    hasValue() {
      return this.multi ? Array.isArray(this.value) && this.value.length > 0 : this.value != null
    },
  },
  watch: {
    isOpen(value) {
      if (value && this.searchable) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    },
    value: {
      handler(newValue) {
        const ids = this.multi ? (Array.isArray(newValue) ? newValue : []) : newValue == null ? [] : [newValue]

        for (const id of this.selectedCache.keys()) {
          if (!ids.includes(id)) this.selectedCache.delete(id)
        }
      },
      immediate: true,
    },
  },
  methods: {
    normalizeSearchValue(value) {
      return String(value == null ? '' : value)
        .toLowerCase()
        .trim()
    },
    toggleOpen() {
      if (this.disabled) return

      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
    },
    async open() {
      this.isOpen = true
      this.$emit('open')
    },
    close() {
      this.isOpen = false
      this.$emit('close')
    },
    closeOnOutside(event) {
      const root = this.$refs.select
      if (!root) return

      const inside = root === event.target || root.contains(event.target)

      if (!inside && this.isOpen) {
        this.close()
      }
    },
    isSelected(option) {
      const optionId = this.resolveOptionId(option)

      if (this.multi) {
        return Array.isArray(this.value) && this.value.includes(optionId)
      }

      return this.value === optionId
    },
    emitValue(selected) {
      this.$emit('input', selected)
      this.$emit('change', selected)
    },
    handleSelectOption(option) {
      const optionId = this.resolveOptionId(option)

      if (this.multi) {
        const selected = Array.isArray(this.value) ? [...this.value] : []
        const index = selected.findIndex(selectedId => selectedId === optionId)

        if (index !== -1) {
          selected.splice(index, 1)
          this.selectedCache.delete(optionId)
        } else {
          selected.push(optionId)
          this.selectedCache.set(optionId, option)
        }

        this.emitValue(selected)
      } else {
        if (this.value === optionId) {
          this.selectedCache.delete(optionId)
          this.emitValue(null)
        } else {
          this.selectedCache.set(optionId, option)
          this.emitValue(optionId)
        }
      }

      if (this.closeOnSelect) {
        this.close()
      }
    },
    handleSearchInput() {
      if (!this.isAsyncMode) return

      this.paginationState.currentPage = 1

      this.$nextTick(() => {
        if (this.$refs.contentSelect) {
          this.$refs.contentSelect.scrollTop = 0
        }
      })

      if (this.shouldBlockSearchByMinLength()) {
        this.remoteOptions = []
        this.paginationState = {
          currentPage: 1,
          pageCount: 1,
        }
        return
      }

      this.debouncedFetch()
    },
    buildRequestParams({ currentPage }) {
      const payload = {
        query: this.searchQuery,
        currentPage: currentPage || this.paginationState.currentPage || this.initialPage,
        limit: this.initialLimit,
      }

      if (typeof this.resolveRequest === 'function') {
        return this.resolveRequest(payload)
      }

      return {
        query: payload.query,
        currentPage: payload.currentPage,
        limit: payload.limit,
      }
    },
    normalizeResponse(response) {
      if (typeof this.resolveResponse === 'function') {
        return this.resolveResponse(response)
      }

      return {
        options: (response.list || []).map(this.mapOption),
        pagination: {
          currentPage: response.pagination?.currentPage || 1,
          pageCount: response.pagination?.pageCount || 1,
        },
      }
    },
    handleDropdownScroll(event) {
      if (!this.isAsyncMode) return
      if (this.loading) return
      if (!this.hasNextPage) return

      const target = event.target
      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight

      if (distanceToBottom <= 15) {
        this.loadNextPage()
      }
    },
    async loadNextPage() {
      if (!this.hasNextPage) return
      if (this.loading) return

      await this.loadOptions({
        currentPage: this.paginationState.currentPage + 1,
        append: true,
      })
    },
    async loadOptions({ currentPage, append = false } = {}) {
      if (!this.isAsyncMode) {
        return
      }

      if (typeof this.options !== 'function') {
        return
      }

      const currentRequestId = ++this.requestId

      this.loading = true

      try {
        const params = this.buildRequestParams({ currentPage })
        const response = await this.options(params)
        const normalized = this.normalizeResponse(response || {})

        if (currentRequestId !== this.requestId) {
          return
        }

        const nextOptions = Array.isArray(normalized.options) ? normalized.options : []

        this.remoteOptions = append ? [...this.remoteOptions, ...nextOptions] : nextOptions

        this.paginationState = {
          currentPage: normalized.pagination?.currentPage || 1,
          pageCount: normalized.pagination?.pageCount || 1,
        }
      } catch (e) {
        if (currentRequestId !== this.requestId) {
          return
        }

        if (!append) {
          this.remoteOptions = []
          this.paginationState = {
            currentPage: 1,
            pageCount: 1,
          }
        }

        this.$emit('error', e)
      } finally {
        if (currentRequestId === this.requestId) {
          this.loading = false
        }
      }
    },
    shouldBlockSearchByMinLength() {
      return this.normalizedQuery.length < this.minSearchLength
    },
    clear() {
      this.selectedCache.clear()
      this.emitValue(this.multi ? [] : null)
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.ui-select
  position: relative
  width: 100%

  &--single
    .ui-select__option
      &._selected
        background: #e5e5e5

  &--disabled
    opacity: var(--field-opacity-disabled, 0.7)
    pointer-events: none

    .ui-select__trigger
      background-color: var(--field-disabled-bg-color, #ededed)

  &--error
    .ui-select
      &__trigger
        padding: 9px 15px
        border: var(--field-error-border-width, 1px) solid var(--field-error-border-color, $error)

  &__trigger
    display: flex
    align-items: center
    justify-content: space-between
    gap: 4px
    min-height: 48px
    padding: 10px 16px
    border: var(--field-border-width, 1px) solid var(--field-border-color, #C8C8C8)
    border-radius: 3px
    background: transparent
    cursor: pointer
    transition: border .2s

  &__value
    flex: 1
    min-width: 0

  &__placeholder
    color: var(--field-placeholder-color, $gray-text)

  &__single-value
    display: block
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    color: var(--field-text-color)

  &__clear
    color: var(--field-placeholder-color, #000)
    transition: color .2s ease
    padding: 5px
    width: 26px

    &:hover
      color: var(--field-icon-color, $cyan)

  &__arrow
    transition: transform .2s ease, margin-right .2s
    color: var(--field-icon-color, $cyan)

    &--open
      transform: rotate(180deg)

  &__dropdown
    position: absolute
    left: 0
    right: 0
    top: calc(100% - 2px)
    z-index: 20
    background: #ffffff
    box-shadow: 0 5px 20px rgba(0, 0, 0, .1)
    border-radius: 0 0 3px 3px
    overflow: hidden

  &__search
    padding: 12px
    border-bottom: 1px solid #e8e8e8

    &-input
      width: 100%
      height: 40px
      padding: 0 12px
      border: var(--field-border-width, 1px) solid #d9d9d9
      border-radius: 3px
      outline: none

  &__content
    max-height: 324px
    overflow-y: auto

  &__list
    padding: 8px 0

  &__search-input, &__list
    color: #2c2c2c

  &__option
    padding: 12px 16px
    cursor: pointer

    &:hover
      background: #f2f2f2

  &__option-inner
    display: flex
    align-items: center
    gap: 12px

  &__option-check
    position: relative
    width: 16px
    height: 16px
    border: var(--field-border-width, 1px) solid #e7e7e8
    border-radius: 4px

  &__option-check-icon
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    opacity: 0
    transition: opacity .2s

    &._visible
      opacity: 1

  &__append-loader
    padding: 12px 16px
    text-align: center
    color: #999

  &__state
    padding: 16px
    color: #999

  &__tags
    display: flex
    flex-wrap: wrap
    gap: 8px

  &__tag
    display: inline-flex
    align-items: center
    gap: 10px
    padding: 2px 8px
    border-radius: 4px
    background: var(--field-chip-bg-color, $cyan-light)
    color: $white

    span
      white-space: nowrap
      max-width: 100px
      overflow: hidden
      text-overflow: ellipsis

  &__tag-remove
    border: 0
    background: transparent
    cursor: pointer
    color: #81d9b7 !important
    transition: color .2s

    &:hover
      color: $white !important
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/fields/UiSelect.vue